import { create } from 'zustand';

import { PendingAction, PendingActionsStore } from './types';

const deriveHiddenEntryIds = (
  pendingActions: Map<string, PendingAction>,
): Set<string> => {
  const ids = new Set<string>();
  for (const action of pendingActions.values()) {
    ids.add(action.entryId);
  }
  return ids;
};

export const usePendingActionsStore = create<PendingActionsStore>()(
  (set, get) => ({
    pendingActions: new Map<string, PendingAction>(),
    hiddenEntryIds: new Set<string>(),

    addPendingAction: (action: PendingAction) => {
      set((state) => {
        const next = new Map(state.pendingActions);
        next.set(action.id, action);
        return {
          pendingActions: next,
          hiddenEntryIds: deriveHiddenEntryIds(next),
        };
      });
    },

    removePendingAction: (actionId: string) => {
      set((state) => {
        const next = new Map(state.pendingActions);
        const action = next.get(actionId);
        if (action) {
          clearTimeout(action.timerId);
          next.delete(actionId);
        }
        return {
          pendingActions: next,
          hiddenEntryIds: deriveHiddenEntryIds(next),
        };
      });
    },

    commitPendingAction: (actionId: string) => {
      set((state) => {
        const next = new Map(state.pendingActions);
        const action = next.get(actionId);
        if (action) {
          clearTimeout(action.timerId);
          next.delete(actionId);
        }
        return {
          pendingActions: next,
          hiddenEntryIds: deriveHiddenEntryIds(next),
        };
      });
    },

    getPendingActionForEntry: (entryId: string) => {
      const { pendingActions } = get();
      for (const action of pendingActions.values()) {
        if (action.entryId === entryId) {
          return action;
        }
      }
      return undefined;
    },

    clearAllPendingActions: () => {
      const { pendingActions } = get();
      const snapshot = new Map(pendingActions);
      for (const action of snapshot.values()) {
        clearTimeout(action.timerId);
      }
      set({ pendingActions: new Map(), hiddenEntryIds: new Set() });
      return snapshot;
    },
  }),
);
